import { TICKS_PER_BAR, TICKS_PER_BEAT } from '../../shared/tempo';
import type { ClassDefinition, UpgradeDefinition } from '../types';

export interface CircadianAttack {
  kind: 'circadian';
  cooldownTicks: number;
  halfBars: number;
  ray: { damage: number; length: number; width: number; spreadDegrees: number };
  crescents: {
    count: number;
    damage: number;
    orbit: number;
    radius: number;
    beatsPerTurn: number;
  };
}

export interface EclipseEffect {
  kind: 'eclipse';
  damage: number;
  radius: number;
  reach: number;
  beats: number;
  slowBeats: number;
  rays: { count: number; damage: number; length: number; width: number };
}

export const CIRCADIEN: ClassDefinition = {
  id: 'circadien',
  name: 'Le Circadien',
  role: 'Un rayon de soleil le jour, des croissants de lune la nuit : son horloge bascule toutes les quatre mesures.',
  color: '#ffc14d',
  maxHp: 120,
  speed: 7.2,
  radius: 14,
  pickupRadius: 110,
  attack: {
    kind: 'circadian',
    cooldownTicks: TICKS_PER_BEAT,
    halfBars: 4,
    ray: { damage: 28, length: 360, width: 16, spreadDegrees: 14 },
    crescents: { count: 3, damage: 24, orbit: 50, radius: 12, beatsPerTurn: 2 },
  },
  skill: {
    id: 'eclipse',
    name: 'Éclipse',
    description:
      'Là où tu vises, l’éclipse frappe, ralentit et éclate en rayons de soleil, puis ton horloge bascule.',
    cooldownTicks: 4 * TICKS_PER_BAR,
    effect: {
      kind: 'eclipse',
      damage: 40,
      radius: 90,
      reach: 220,
      beats: 1,
      slowBeats: 2,
      rays: { count: 12, damage: 20, length: 720, width: 16 },
    },
  },
  affinityWeaponIds: ['baton-de-feu'],
};

export const CIRCADIEN_UPGRADES: readonly UpgradeDefinition[] = [
  {
    id: 'plein-ciel',
    name: 'Plein ciel',
    description: 'Tu gagnes un rayon le jour et un croissant la nuit.',
    family: 'class',
    classId: 'circadien',
    modifiers: [{ stat: 'projectileCountAdd', add: 1 }],
    rarities: {
      rare: {
        description: 'Tu gagnes deux rayons le jour et deux croissants la nuit.',
        modifiers: [{ stat: 'projectileCountAdd', add: 2 }],
      },
      legendary: {
        description: 'Tu gagnes trois rayons le jour, trois croissants la nuit.',
        modifiers: [{ stat: 'projectileCountAdd', add: 3 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'grands-astres',
    name: 'Grands astres',
    description: 'Ton rayon s’élargit et tes croissants grossissent.',
    family: 'class',
    classId: 'circadien',
    modifiers: [{ stat: 'attackRadiusMul', mul: 1.4 }],
    rarities: {
      rare: {
        description: 'Ton rayon s’élargit bien et tes croissants grossissent bien.',
        modifiers: [{ stat: 'attackRadiusMul', mul: 1.8 }],
      },
      legendary: {
        description: 'Ton rayon devient immense et tes croissants énormes.',
        modifiers: [{ stat: 'attackRadiusMul', mul: 2.3 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'eclipse-totale',
    name: 'Éclipse totale',
    description: 'L’ombre de ton éclipse couvre plus large.',
    family: 'class',
    classId: 'circadien',
    modifiers: [{ stat: 'skillRadiusMul', mul: 1.3 }],
    rarities: {
      rare: {
        description: 'L’ombre de ton éclipse couvre bien plus large.',
        modifiers: [{ stat: 'skillRadiusMul', mul: 1.6 }],
      },
      legendary: {
        description: 'L’ombre de ton éclipse couvre deux fois plus large.',
        modifiers: [{ stat: 'skillRadiusMul', mul: 2 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'entre-chien-et-loup',
    name: 'Entre chien et loup',
    description: 'Rayon et croissants ensemble une mesure après ta bascule.',
    family: 'class',
    classId: 'circadien',
    modifiers: [{ stat: 'twilightBeatsAdd', add: 4 }],
    rarities: {
      rare: {
        description: 'Rayon et croissants ensemble 1,5 mesure après ta bascule.',
        modifiers: [{ stat: 'twilightBeatsAdd', add: 6 }],
      },
      legendary: {
        description: 'Rayon et croissants ensemble 2 mesures après ta bascule.',
        modifiers: [{ stat: 'twilightBeatsAdd', add: 8 }],
      },
    },
    maxStacks: 2,
  },
];
